"use client";

import { Button } from "@heroui/button";
import { button as buttonStyles } from "@heroui/theme";
import clsx from "clsx";

type Provider = "google" | "kakao";

interface OAuthProviderButtonProps {
  provider: Provider;
  label: string;
  /**
   * Render a real link (full page navigation, works without JavaScript)
   * instead of a button, e.g. for server-side OAuth flows.
   */
  href?: string;
  onClick?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

const providerStyles: Record<Provider, string> = {
  google: "bg-[#4285F4] text-white hover:bg-[#357ae8]",
  kakao: "bg-[#FEE500] text-black hover:bg-[#fdd800]",
};

const providerIcons: Record<Provider, React.ReactNode> = {
  google: (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  ),
  kakao: (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M12 3C6.477 3 2 6.327 2 10.5c0 2.722 1.791 5.117 4.479 6.531-.192.71-.689 2.565-.793 2.968-.124.482.176.475.372.345.157-.104 2.542-1.705 3.59-2.413.782.107 1.58.163 2.352.163 5.523 0 10-3.327 10-7.5S17.523 3 12 3z" />
    </svg>
  ),
};

export default function OAuthProviderButton({
  provider,
  label,
  href,
  onClick,
  isLoading = false,
  isDisabled = false,
}: OAuthProviderButtonProps) {
  if (href) {
    return (
      <a
        className={buttonStyles({
          fullWidth: true,
          size: "lg",
          variant: "flat",
          className: providerStyles[provider],
        })}
        href={href}
        onClick={onClick}
      >
        {providerIcons[provider]}
        {label}
      </a>
    );
  }

  return (
    <Button
      className={clsx("w-full", providerStyles[provider])}
      isDisabled={isDisabled}
      isLoading={isLoading}
      size="lg"
      startContent={isLoading ? null : providerIcons[provider]}
      variant="flat"
      onPress={onClick}
    >
      {label}
    </Button>
  );
}
